import React, { useEffect } from 'react';
import { Check, X } from 'lucide-react';

/**
 * A minimalist, professional notification component for feedback
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the notification
 * @param {function} props.onClose - Function to call when notification is closed
 * @param {string} props.type - Type of notification ('success' or 'error')
 * @param {string} props.message - Message to display
 * @param {number} props.autoCloseTime - Time in ms after which notification auto-closes (default: 3000)
 */
const FeedbackNotification = ({ 
  show, 
  onClose, 
  type = 'success', 
  message, 
  autoCloseTime = 3000 
}) => {
  useEffect(() => {
    if (show && autoCloseTime) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseTime);
      
      return () => clearTimeout(timer);
    }
  }, [show, onClose, autoCloseTime]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div 
        className="absolute inset-0 bg-black bg-opacity-20" 
        onClick={onClose}
      />
      <div 
        className={`transform transition-all duration-300 ease-in-out translate-y-0 opacity-100 bg-white rounded-md shadow-lg py-3 px-4 max-w-sm mx-auto flex items-center`}
      >
        {type === 'success' && (
          <div className="flex items-center space-x-3 w-full">
            <div className="bg-primary bg-opacity-10 p-1.5 rounded-full">
              <Check className="h-5 w-5 text-primary" />
            </div>
            <p className="text-sm font-medium text-gray-700 flex-1">{message}</p>
          </div>
        )}
        {type === 'error' && (
          <div className="flex items-center space-x-3 w-full">
            <div className="bg-red-50 p-1.5 rounded-full">
              <X className="h-5 w-5 text-red-500" />
            </div>
            <p className="text-sm font-medium text-gray-700 flex-1">{message}</p>
          </div>
        )}
        <button 
          onClick={onClose} 
          className="ml-3 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default FeedbackNotification;
